import React, { useRef, useState } from 'react';
import { Alert, Box, Button, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import Dialog from './ActionDialog';
import { useTranslation } from 'react-i18next';
import { useSubscription } from '../context/SubscriptionContext';
import { FACEBOOK_MESSENGER_URL } from '../constants/commonConstants';

export default function SubscriptionDialog({ open, onClose }) {
    const { t } = useTranslation();
    const { data, active, refresh } = useSubscription();
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState(null);
    const operation = useRef(null);
    const run = async (endpoint, planId) => {
        if (operation.current?.busy) return;
        const key = `${endpoint}:${planId || ''}`;
        if (operation.current?.key !== key) operation.current = { key, requestId: crypto.randomUUID() };
        operation.current.busy = true;
        setBusy(true); setMessage(null);
        try {
            const { default: api } = await import('../services/Api');
            await api.post(`/api/subscriptions/${endpoint}`, { planId, requestId: operation.current.requestId });
            operation.current = null;
            await refresh(); setMessage({ severity: 'success', text: t('subscription.success') });
        } catch (error) {
            const unavailable = error?.response?.data?.code === 'FEATURE_UNAVAILABLE';
            setMessage({ severity: 'error', text: t(unavailable ? 'subscription.unavailable' : 'subscription.failed') });
        } finally { if (operation.current) operation.current.busy = false; setBusy(false); }
    };
    return <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
        <DialogTitle>{t('subscription.title')}</DialogTitle>
        <DialogContent>
            <Typography>{t(active ? 'subscription.active' : 'subscription.inactive')}</Typography>
            {message && <Alert severity={message.severity}>{message.text}</Alert>}
            {data?.showSubscribe && !data?.isAdmin && data.doExternalSubscribe && <>
                <Button component="a" href={FACEBOOK_MESSENGER_URL} target="_blank" rel="noopener noreferrer"
                    variant="contained" sx={{ mt: 1 }}>{t('subscription.messenger')}</Button>
            </>}
            {data?.showSubscribe && !data?.isAdmin && !data.doExternalSubscribe && <>
                {data.trialMode === 'BeforePayment' && data.trialMonths > 0 && !data.subscription?.usedTrial &&
                    <Button disabled={busy} onClick={() => run('trial')}>{t('subscription.claimTrial')}</Button>}
                <Box sx={{ display: 'grid', gap: 1, mt: 1 }}>
                    {data.plans?.map(plan => <Button key={plan.id} variant="outlined" disabled={busy} onClick={() => run('checkout', plan.id)}>
                        {t('subscription.plan', { months: plan.months })} — {new Intl.NumberFormat(undefined, { style: 'currency', currency: plan.currency }).format(plan.price)}
                    </Button>)}
                </Box>
            </>}
        </DialogContent>
        <DialogActions><Button disabled={busy} onClick={onClose}>{t('common.close')}</Button></DialogActions>
    </Dialog>;
}
